import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  InputBase,
  Badge,
  IconButton,
  Box,
  Button,
  Menu,
  MenuItem,
  alpha,
} from '@mui/material';
import {
  Search as SearchIcon,
  ShoppingCart,
  AccountCircle,
  Logout,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { getUnreadNotificationCount } from '../services/marketplaceService';

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: 8,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
    border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
  },
  '&:focus-within': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
    border: `1px solid ${alpha(theme.palette.common.white, 0.4)}`,
    boxShadow: `0 0 0 2px ${alpha(theme.palette.common.white, 0.1)}`,
  },
  marginRight: theme.spacing(3),
  marginLeft: theme.spacing(2),
  width: '100%',
  maxWidth: 500,
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(4),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 1.5),
  height: '100%',
  position: 'absolute',
  right: 2,
  top: 2,
  bottom: 2,
  width: 44,
  pointerEvents: 'auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  borderRadius: '0 6px 6px 0',
  transition: theme.transitions.create(['background-color', 'color'], {
    duration: theme.transitions.duration.short,
  }),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    color: theme.palette.common.white,
  },
  '&:active': {
    backgroundColor: alpha(theme.palette.common.white, 0.2),
  },
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1.25, 6, 1.25, 2),
    paddingRight: theme.spacing(6),
    transition: theme.transitions.create(['width', 'padding'], {
      duration: theme.transitions.duration.short,
    }),
    width: '100%',
    fontSize: '0.95rem',
    fontWeight: 400,
    '&::placeholder': {
      color: alpha(theme.palette.common.white, 0.7),
      opacity: 1,
    },
    '&:focus': {
      '&::placeholder': {
        color: alpha(theme.palette.common.white, 0.5),
      },
    },
    [theme.breakpoints.up('md')]: {
      width: '40ch',
    },
  },
}));

const Header = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const { getCartCount, toggleCart } = useCart();

  // Poll unread notification count every 60 seconds
  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchCount = () => getUnreadNotificationCount().then(setUnreadCount);
    fetchCount();
    const interval = setInterval(fetchCount, 60 * 1000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
    navigate('/');
  };

  const handleLogin = () => {
    handleMenuClose();
    navigate('/login');
  };

  const handleRegister = () => {
    handleMenuClose();
    navigate('/register');
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      navigate(`/parts?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <AppBar position="static" color="primary">
      <Toolbar>
        <Typography
          variant="h6"
          component={Link}
          to="/"
          sx={{
            flexGrow: 0,
            textDecoration: 'none',
            fontWeight: 'bold',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
        >
          <Box
            component="img"
            src="/logo.png"
            alt="PartzFlow Logo"
            sx={{
              height: 45,
              width: 'auto',
              display: { xs: 'none', sm: 'block' },
              filter: 'brightness(0) invert(1)',
            }}
          />
          PartzFlow
        </Typography>

        <Search>
          <StyledInputBase
            placeholder="Search components..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleSearchKeyPress}
            inputProps={{ 'aria-label': 'search' }}
          />
          <SearchIconWrapper onClick={handleSearch}>
            <SearchIcon />
          </SearchIconWrapper>
        </Search>

        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            color="inherit"
            sx={{
              fontSize: '0.9rem',
              fontWeight: 500,
              px: 2,
              py: 1,
              borderRadius: 2,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: alpha('#ffffff', 0.1),
              }
            }}
          >
            Categories
          </Button>


          {/* Notification Bell — authenticated only */}
          {isAuthenticated && (
            <IconButton
              color="inherit"
              onClick={() => navigate('/notifications')}
              sx={{
                ml: 1, p: 1.5, borderRadius: 2,
                '&:hover': { backgroundColor: alpha('#ffffff', 0.1) },
              }}
            >
              <Badge badgeContent={unreadCount || null} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          )}

          <IconButton
            color="inherit"
            onClick={toggleCart}
            sx={{
              ml: 1, p: 1.5, borderRadius: 2,
              '&:hover': { backgroundColor: alpha('#ffffff', 0.1) },
            }}
          >
            <Badge badgeContent={getCartCount()} color="error">
              <ShoppingCart />
            </Badge>
          </IconButton>

          <IconButton
            edge="end"
            onClick={handleProfileMenuOpen}
            color="inherit"
            sx={{
              ml: 0.5,
              p: 1.5,
              borderRadius: 2,
              '&:hover': {
                backgroundColor: alpha('#ffffff', 0.1),
              }
            }}
          >
            <AccountCircle />
          </IconButton>
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          {isAuthenticated ? (
            <>
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary">
                  {user?.email || user?.name}
                </Typography>
              </MenuItem>
              <MenuItem onClick={() => { navigate('/profile'); handleMenuClose(); }}>My Profile</MenuItem>
              <MenuItem onClick={() => { navigate('/my-shop'); handleMenuClose(); }}>My Shop</MenuItem>
              <MenuItem onClick={() => { navigate('/orders'); handleMenuClose(); }}>My Purchases</MenuItem>
              <MenuItem onClick={() => { navigate('/favorites'); handleMenuClose(); }}>My Favorites</MenuItem>
              <MenuItem onClick={() => { navigate('/messages'); handleMenuClose(); }}>Messages</MenuItem>
              <MenuItem onClick={() => { navigate('/notifications'); handleMenuClose(); }}>
                Notifications {unreadCount > 0 && `(${unreadCount})`}
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <Logout fontSize="small" sx={{ mr: 1 }} />
                Logout
              </MenuItem>
            </>
          ) : (
            <>
              <MenuItem onClick={handleLogin}>Login</MenuItem>
              <MenuItem onClick={handleRegister}>Register</MenuItem>
            </>
          )}
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Header;